import React, { useState, useEffect, useRef } from 'react';
import { WarehouseNode, Product, InventoryLevel, DemandData, DemandGap } from '../../types';
import './AIAssistant.css';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isLoading?: boolean;
}

interface ChatSession {
  session_id: string;
  title: string;
  created_at: string;
  messages: ChatMessage[];
}

interface AIAssistantProps {
  isVisible: boolean;
  onToggle: () => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ isVisible, onToggle }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sam-chatbot API configuration
  const SAM_CHATBOT_API_URL = 'https://peuihf4klf.execute-api.us-east-1.amazonaws.com/dev/api/v1';

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  // Initialize session when component mounts
  useEffect(() => {
    if (isVisible && !currentSession) {
      initializeSession();
    }
  }, [isVisible]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeSession = async () => {
    try {
      setError(null);
      const response = await fetch(`${SAM_CHATBOT_API_URL}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: `Warehouse Optimization Assistant - ${new Date().toLocaleDateString()}`,
          system_prompt: `You are an AI assistant specialized in warehouse optimization, inventory management, and supply chain operations. You provide context-aware analysis based on the current page the user is viewing in the Warehouse Optimization System.

ROLE AND CAPABILITIES:
Your role is to answer user questions about the data currently displayed on any page of the Warehouse Optimization System and provide clear, actionable insights.

CONTEXT-AWARE OPERATION:
You receive structured context data extracted from the user's current page view, which may include:

1. **INVENTORY MANAGEMENT PAGE**:
   - Filter state (active tab, selections)
   - Master data (warehouse nodes, products)  
   - Transactional data (inventory levels, demand forecasts, demand gaps)
   - Calculated summary metrics

2. **WAREHOUSE NETWORK PAGE**:
   - Warehouse nodes configuration
   - Capacity and location information
   - Active/inactive status

3. **OPTIMIZATION ENGINE PAGE**:
   - Available products and solvers
   - Recent optimization results
   - Solver capabilities and performance

4. **S&OP PLANNING PAGE**:
   - Planning scenarios and data
   - Time horizons and forecasts
   - Strategic planning context

5. **HOME/DASHBOARD PAGE**:
   - General system overview
   - Navigation guidance

RESPONSE GUIDELINES:

1. **DATA-DRIVEN ANALYSIS**:
   - Context data is the source of truth for all calculations and answers
   - When users ask about quantities, trends, or comparisons, compute answers directly from context data
   - Show specific numbers, percentages, and calculations
   - Reference the exact data source and current page context
   - Provide context about current filters and view state

2. **WHEN DATA IS INSUFFICIENT**:
   - If the answer cannot be derived from context data, say so explicitly
   - Explain what additional data would be required
   - Guide users to the appropriate page where that data is available
   - Do not invent or assume data beyond what is provided

3. **RESPONSE STRUCTURE**:
   - **Current Page Analysis**: Specific calculations and insights from context data
   - **Actionable Insights**: Concrete recommendations based on the numbers  
   - **Navigation Guidance**: Direct users to relevant pages when needed
   - **General Best Practices**: Industry-standard principles (clearly separated)

4. **PAGE-SPECIFIC EXPERTISE**:
   - **Inventory**: Gap analysis, shortage/surplus identification, demand forecasting
   - **Network**: Capacity planning, coverage analysis, node optimization
   - **Optimization**: Algorithm selection, result interpretation, allocation strategies
   - **S&OP**: Scenario planning, strategic forecasting, operations alignment
   - **General**: System navigation, workflow guidance, best practices

5. **AREAS OF EXPERTISE**:
   - Warehouse inventory optimization strategies
   - Supply chain analysis and recommendations
   - S&OP (Sales & Operations Planning) guidance  
   - Demand forecasting and gap analysis
   - Optimization algorithm explanations
   - Best practices for warehouse operations
   - System navigation and workflow optimization

Always provide clear, actionable insights and explain complex concepts in an accessible way. When context data is not available for the current page, focus on general guidance and direct users to the appropriate sections where relevant data can be found.`,
          metadata: {
            type: 'warehouse_optimization',
            domain: 'supply_chain',
            created_by: 'warehouse_optimization_frontend',
            context_aware: true
          }
        }),
      });

      if (response.ok) {
        const sessionData = await response.json();
        const newSession: ChatSession = {
          session_id: sessionData.session_id,
          title: sessionData.title,
          created_at: sessionData.created_at,
          messages: []
        };
        setCurrentSession(newSession);
        
        // Add welcome message
        const welcomeMessage: ChatMessage = {
          id: 'welcome',
          type: 'assistant',
          content: `Hello! I'm your Warehouse Optimization AI Assistant. I can analyze ALL data from any page you're currently viewing and provide detailed insights about:

• **Inventory Management Page**: Complete analysis of every inventory record, demand forecast, and gap calculation
• **Warehouse Network Page**: Detailed insights about each warehouse node, capacity, status, and configuration
• **Optimization Engine Page**: Full analysis of optimization results, solver performance, and allocation details  
• **S&OP Planning Page**: Comprehensive planning data including flow analysis, scenario details, and forecasting
• **General System Help**: Navigation guidance and best practices for warehouse optimization

**Complete Data Access**: I can see ALL your data - not just summaries. This means I can:
- Answer questions about specific warehouse nodes, products, or records
- Calculate detailed metrics using your complete dataset
- Identify specific issues, trends, and opportunities
- Provide precise recommendations based on individual data points

**Enhanced Analysis Capabilities**:
- *Inventory*: "Show me all shortages for Product A" "Which specific nodes have surplus inventory?"
- *Network*: "List all inactive nodes with their details" "Calculate total network capacity"
- *Optimization*: "Analyze the allocation details from my last optimization" "Compare solver performance"
- *S&OP*: "Show me the complete flow data for Warehouse B" "Analyze inventory trends over 13 weeks"

**How it works**: 
- Navigate to any page in the system
- Ask me detailed questions about ANY data you see
- I'll analyze your complete dataset and provide specific insights
- Get precise calculations, comparisons, and actionable recommendations

Ask me anything about your warehouse optimization system - I have access to all your data!`,
          timestamp: new Date().toISOString()
        };
        setMessages([welcomeMessage]);
      } else {
        throw new Error(`Failed to create session: ${response.status}`);
      }
    } catch (err) {
      console.error('Error initializing AI session:', err);
      setError('Failed to connect to AI assistant. Please try again later.');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentSession || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      type: 'assistant',
      content: 'Thinking...',
      timestamp: new Date().toISOString(),
      isLoading: true
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      // Get current page context
      const currentView = (window as any).currentActiveView || 'home';
      let contextData = null;
      let systemContext = '';
      
      // Extract context based on current page
      switch (currentView) {
        case 'inventory':
          contextData = (window as any).warehouseContextData;
          if (contextData) {
            systemContext = `
WAREHOUSE INVENTORY OPTIMIZATION CONTEXT:

Current Page: Inventory Management (${contextData.filters.active_tab} tab)

SUMMARY METRICS:
- Total Inventory: ${contextData.summary.total_inventory} units
- Total Demand: ${contextData.summary.total_demand} units  
- Total Shortage: ${contextData.summary.total_shortage} units
- Total Surplus: ${contextData.summary.total_surplus} units
- Nodes with Shortage: ${contextData.summary.nodes_with_shortage}/${contextData.nodes.length}
- Products with Shortage: ${contextData.summary.products_with_shortage}/${contextData.products.length}

WAREHOUSE NODES (${contextData.nodes.length}):
${contextData.nodes.map((node: WarehouseNode) => `- ${node.name} (${node.node_id}): ${node.location}${node.capacity ? `, Capacity: ${node.capacity}` : ''}${node.active ? ', Active' : ', Inactive'}`).join('\n')}

PRODUCTS (${contextData.products.length}):
${contextData.products.map((product: Product) => `- ${product.name} (${product.product_id})${product.category ? `, Category: ${product.category}` : ''}`).join('\n')}

COMPLETE INVENTORY DATA (${contextData.inventory.length} records):
${contextData.inventory.map((inv: InventoryLevel) => `- Node ${inv.node_id}, Product ${inv.product_id}: ${inv.quantity} units (Updated: ${inv.last_updated}${inv.updated_by ? ` by ${inv.updated_by}` : ''})`).join('\n')}

COMPLETE DEMAND DATA (${contextData.demand.length} records):
${contextData.demand.map((dem: DemandData) => `- Node ${dem.node_id}, Product ${dem.product_id}: ${dem.quantity} units demand (Period: ${dem.forecast_period}, Updated: ${dem.last_updated})`).join('\n')}

COMPLETE DEMAND GAPS DATA (${contextData.demand_gaps.length} records):
${contextData.demand_gaps.map((gap: DemandGap) => 
  `- Node ${gap.node_id}, Product ${gap.product_id}: Inventory=${gap.inventory_quantity}, Demand=${gap.demand_quantity}, Gap=${gap.gap} (${gap.gap > 0 ? 'SHORTAGE' : gap.gap < 0 ? 'SURPLUS' : 'BALANCED'})`
).join('\n')}

INSTRUCTIONS:
- Use this complete context_data as the source of truth for all calculations and answers
- You have access to ALL inventory, demand, and gap records - not just summaries
- When users ask about specific nodes, products, or combinations, reference the exact data above
- Calculate totals, averages, and comparisons using the complete dataset
- Identify specific shortages, surpluses, and trends from individual records
- If the answer cannot be derived from this context_data, say so explicitly and explain what additional data would be required
- Separate numeric analysis from general best practices recommendations
- Be specific with numbers and reference the exact data source and record details
`;
          }
          break;
          
        case 'nodes':
          const nodesData = (window as any).warehouseNodesData;
          if (nodesData) {
            systemContext = `
WAREHOUSE NODES MANAGEMENT CONTEXT:

Current Page: Warehouse Network Management

COMPLETE WAREHOUSE NODES DATA (${nodesData.length} nodes):
${nodesData.map((node: WarehouseNode) => 
  `- ${node.name} (${node.node_id}): 
  Location: ${node.location}
  Capacity: ${node.capacity || 'Not set'}
  Status: ${node.active ? 'Active' : 'Inactive'}
  Created: ${node.created_at}
  Updated: ${node.updated_at}`
).join('\n')}

NETWORK ANALYSIS:
- Total Nodes: ${nodesData.length}
- Active Nodes: ${nodesData.filter((n: WarehouseNode) => n.active).length}
- Inactive Nodes: ${nodesData.filter((n: WarehouseNode) => !n.active).length}
- Nodes with Capacity Set: ${nodesData.filter((n: WarehouseNode) => n.capacity).length}
- Nodes without Capacity: ${nodesData.filter((n: WarehouseNode) => !n.capacity).length}
- Total Network Capacity: ${nodesData.reduce((sum: number, n: WarehouseNode) => sum + (n.capacity || 0), 0)} units

INSTRUCTIONS:
- Use this complete nodes data to answer questions about warehouse network configuration
- You have access to ALL node details including capacity, status, location, and timestamps
- Provide insights about network coverage, capacity planning, and node management
- Calculate network-wide metrics using the complete dataset
- Identify specific nodes that need attention (inactive, no capacity, etc.)
- If asked about inventory or demand data, explain that this information is available on the Inventory Management page
`;
          } else {
            systemContext = `
WAREHOUSE NODES MANAGEMENT CONTEXT:

Current Page: Warehouse Network Management

No warehouse nodes data is currently available. The user may need to:
1. Create warehouse nodes first
2. Refresh the page to load existing nodes
3. Check network connectivity

INSTRUCTIONS:
- Guide the user on how to create and manage warehouse nodes
- Explain the importance of warehouse network setup for inventory optimization
- Suggest next steps for setting up their warehouse network
`;
          }
          break;
          
        case 'optimization':
          const optimizationData = (window as any).warehouseOptimizationData;
          if (optimizationData) {
            systemContext = `
OPTIMIZATION ENGINE CONTEXT:

Current Page: Optimization Engine

AVAILABLE PRODUCTS (${optimizationData.products?.length || 0}):
${optimizationData.products?.map((product: Product) => 
  `- ${product.name} (${product.product_id})${product.category ? `, Category: ${product.category}` : ''}${product.unit_of_measure ? `, Unit: ${product.unit_of_measure}` : ''}`
).join('\n') || 'No products available'}

SOLVER INFORMATION:
- Simple Solver Available: ${optimizationData.solver_info?.simple_available ? 'Yes' : 'No'}
- Advanced Solver Available: ${optimizationData.solver_info?.advanced_available ? 'Yes' : 'No'}
- Default Solver: ${optimizationData.solver_info?.default_solver || 'Unknown'}
- Simple Solver Features: ${optimizationData.solver_info?.simple_features?.join(', ') || 'Not specified'}
- Advanced Solver Features: ${optimizationData.solver_info?.advanced_features?.join(', ') || 'Not specified'}

RECENT OPTIMIZATION RESULTS (${optimizationData.recent_results?.length || 0} results):
${optimizationData.recent_results?.map((result: any) => 
  `- Optimization ID: ${result.optimization_id}
  Timestamp: ${result.timestamp}
  Status: ${result.status}
  Product: ${result.input_data?.product_id || 'Unknown'}
  Purchase Quantity: ${result.input_data?.purchase_quantity || 'Unknown'}
  Allocation Strategy: ${result.input_data?.allocation_strategy || 'Unknown'}
  Solver Used: ${result.solver_used || 'Unknown'}
  Total Demand Gap Reduction: ${result.total_demand_gap_reduction}
  Objective Value: ${result.objective_value}
  Solve Time: ${result.solve_time_seconds} seconds
  Allocations: ${result.allocations?.length || 0} nodes
  ${result.allocations?.map((alloc: any) => 
    `    - Node ${alloc.node_id}: ${alloc.allocated_quantity} units (Before: ${alloc.before_inventory}, After: ${alloc.after_inventory}, Gap Reduction: ${alloc.demand_gap_reduction})`
  ).join('\n  ') || ''}`
).join('\n') || 'No recent optimization results'}

NODE NAME MAPPING:
${Object.entries(optimizationData.node_name_map || {}).map(([nodeId, nodeName]) => 
  `- ${nodeId}: ${nodeName}`
).join('\n')}

CURRENT STATUS:
- Loading: ${optimizationData.is_loading ? 'Yes' : 'No'}
- Error: ${optimizationData.error || 'None'}

INSTRUCTIONS:
- Use this complete optimization data to answer questions about solver capabilities and optimization results
- You have access to ALL optimization result details including allocations, performance metrics, and solver information
- Help users understand optimization results and solver capabilities
- Explain allocation strategies and optimization algorithms based on actual results
- Guide users on how to interpret optimization results and make decisions
- Calculate optimization performance metrics using the complete dataset
`;
          } else {
            systemContext = `
OPTIMIZATION ENGINE CONTEXT:

Current Page: Optimization Engine

No optimization data is currently available. The user may need to:
1. Set up warehouse nodes first
2. Input inventory and demand data
3. Run an optimization to see results

INSTRUCTIONS:
- Guide the user through the optimization process
- Explain optimization concepts and algorithms
- Help troubleshoot optimization issues
`;
          }
          break;
          
        case 'sop':
          const sopData = (window as any).warehouseSOPData;
          if (sopData) {
            systemContext = `
S&OP PLANNING CONTEXT:

Current Page: Sales & Operations Planning (${sopData.active_view} view)

PLANNING OVERVIEW:
- Current Week: ${sopData.planning_data?.current_week || 'Not set'}
- Planning Horizon: ${sopData.planning_data?.horizon || '52 weeks'}
- Active View: ${sopData.active_view}
- Available Scenarios: ${sopData.planning_data?.scenarios?.length || 0}

DASHBOARD DATA:
${sopData.dashboard_data ? `
- Selected Warehouse: ${sopData.dashboard_data.selected_warehouse || 'All'}
- Start Week: ${sopData.dashboard_data.start_week || 'Not set'}
- Warehouses: ${sopData.dashboard_data.warehouses?.length || 0}
- Planning Weeks: ${sopData.dashboard_data.planning_weeks || 13}

WAREHOUSE FLOW DATA:
${sopData.dashboard_data?.warehouses?.map((warehouse: string) => 
  `- ${warehouse}:
    Inbound Flow: ${sopData.dashboard_data.inbound_data?.[warehouse]?.slice(0, 3).join(', ') || 'No data'}... (${sopData.dashboard_data.inbound_data?.[warehouse]?.length || 0} weeks)
    Outbound Flow: ${sopData.dashboard_data.outbound_data?.[warehouse]?.slice(0, 3).join(', ') || 'No data'}... (${sopData.dashboard_data.outbound_data?.[warehouse]?.length || 0} weeks)
    Inventory Levels: ${sopData.dashboard_data.inventory_data?.[warehouse]?.slice(0, 3).join(', ') || 'No data'}... (${sopData.dashboard_data.inventory_data?.[warehouse]?.length || 0} weeks)`
).join('\n') || 'No warehouse flow data available'}` : 'No dashboard data available'}

SCENARIO DATA:
${sopData.scenario_data ? `
- Active Scenarios: ${sopData.scenario_data.scenarios?.length || 0}
- Comparison Mode: ${sopData.scenario_data.comparison_mode || 'Disabled'}
- What-If Parameters: ${sopData.scenario_data.whatif_parameters ? Object.keys(sopData.scenario_data.whatif_parameters).join(', ') : 'None'}` : 'No scenario data available'}

INSIGHTS DATA:
${sopData.insights_data ? `
- AI Insights Available: ${sopData.insights_data.insights?.length || 0}
- Recommendations: ${sopData.insights_data.recommendations?.length || 0}
- Analysis Period: ${sopData.insights_data.analysis_period || 'Not set'}` : 'No insights data available'}

INSTRUCTIONS:
- Use this S&OP planning data to answer questions about strategic planning and forecasting
- You have access to warehouse flow data, scenario information, and planning parameters
- Help users with S&OP planning concepts and strategies
- Explain scenario planning and time-series analysis based on available data
- Guide users on demand forecasting and capacity planning
- Calculate planning metrics using the complete dataset when available
- If specific planning data is not available, guide users on how to generate or input the required data
`;
          } else {
            systemContext = `
S&OP PLANNING CONTEXT:

Current Page: Sales & Operations Planning

No S&OP data is currently available. The user may need to:
1. Set up warehouse nodes and inventory data first
2. Configure planning parameters
3. Create planning scenarios

INSTRUCTIONS:
- Guide the user through S&OP planning setup
- Explain S&OP concepts and best practices
- Help with scenario planning and forecasting
`;
          }
          break;
          
        case 'home':
        default:
          systemContext = `
WAREHOUSE OPTIMIZATION SYSTEM CONTEXT:

Current Page: Home/Dashboard

The user is on the main dashboard page. Available system modules:
1. Warehouse Network Management - Configure warehouse nodes and locations
2. Inventory Management - Manage inventory levels and customer demand
3. Optimization Engine - Run optimization algorithms for allocation
4. S&OP Planning - Strategic sales and operations planning

INSTRUCTIONS:
- Provide general guidance about the warehouse optimization system
- Help users navigate to appropriate sections based on their needs
- Explain system capabilities and workflow
- If users ask about specific data, guide them to the relevant page where that data is available
`;
          break;
      }
      
      // Prepare message with context
      let messageWithContext = userMessage.content;
      
      if (systemContext) {
        messageWithContext = `${systemContext}\n\nUSER QUESTION: ${userMessage.content}`;
      }

      const response = await fetch(`${SAM_CHATBOT_API_URL}/sessions/${currentSession.session_id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageWithContext,
          use_rag: true,
          metadata: {
            context: 'warehouse_optimization',
            current_page: currentView,
            timestamp: userMessage.timestamp,
            has_context_data: !!contextData,
            active_tab: contextData?.filters?.active_tab || 'unknown'
          }
        }),
      });

      if (response.ok) {
        const messageData = await response.json();
        const assistantMessage: ChatMessage = {
          id: messageData.message_id || `assistant-${Date.now()}`,
          type: 'assistant',
          content: messageData.message || messageData.response,
          timestamp: messageData.created_at || new Date().toISOString()
        };

        // Replace loading message with actual response
        setMessages(prev => prev.filter(msg => !msg.isLoading).concat([assistantMessage]));
      } else {
        throw new Error(`Failed to send message: ${response.status}`);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
      // Remove loading message on error
      setMessages(prev => prev.filter(msg => !msg.isLoading));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = async () => {
    setMessages([]);
    setCurrentSession(null);
    setError(null);
    await initializeSession();
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!isVisible) return null;

  return (
    <div className="ai-assistant-panel">
      <div className="ai-assistant-header">
        <div className="header-content">
          <div className="assistant-title">
            <div className="ai-icon">🤖</div>
            <div>
              <h3>AI Assistant</h3>
              <span className="session-info">
                {currentSession ? 'Connected' : 'Connecting...'}
              </span>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="btn-icon"
              onClick={clearConversation}
              title="New Conversation"
              disabled={isLoading}
            >
              🔄
            </button>
            <button
              className="btn-icon close-btn"
              onClick={onToggle}
              title="Close Assistant"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      <div className="ai-assistant-content">
        <div className="messages-container">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.type} ${message.isLoading ? 'loading' : ''}`}
            >
              <div className="message-content">
                {message.isLoading ? (
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <div className="message-text">
                    {message.content.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < message.content.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              <div className="message-timestamp">
                {formatTimestamp(message.timestamp)}
              </div>
            </div>
          ))}
          {error && (
            <div className="error-message">
              <div className="error-content">
                ⚠️ {error}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about warehouse optimization, inventory management, or S&OP planning..."
              disabled={isLoading || !currentSession}
              className="message-input"
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || !currentSession}
              className="send-button"
              title="Send message"
            >
              {isLoading ? '⏳' : '➤'}
            </button>
          </div>
          <div className="input-hint">
            Press Enter to send • Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;